import { useRef, useState } from "react";
import { UploadCloud, CheckCircle2, AlertCircle, Loader2, X } from "lucide-react";
import api from "../../lib/api";

const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/heic"];
const MAX_BYTES = 10 * 1024 * 1024;

export default function MultiUploadDropzone({ categories, onComplete }) {
  const fileRef = useRef(null);
  const [category, setCategory] = useState(categories[0]?.id || "famille");
  const [dragOver, setDragOver] = useState(false);
  const [queue, setQueue] = useState([]); // {key, name, size, status, error, url}
  const [busy, setBusy] = useState(false);

  const validate = (file) => {
    if (!ACCEPTED.includes(file.type)) return "Format non supporté";
    if (file.size > MAX_BYTES) return "Image > 10 Mo";
    return null;
  };

  const startUploads = async (files) => {
    const initial = files.map((f, i) => {
      const err = validate(f);
      return {
        key: `${Date.now()}-${i}-${f.name}`,
        name: f.name,
        size: f.size,
        status: err ? "error" : "queued",
        error: err,
        file: err ? null : f,
      };
    });
    setQueue((q) => [...q, ...initial]);
    setBusy(true);

    let createdCount = 0;
    for (const item of initial) {
      if (item.status === "error") continue;
      setQueue((q) => q.map((x) => (x.key === item.key ? { ...x, status: "uploading" } : x)));
      try {
        const fd = new FormData();
        fd.append("file", item.file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post("/gallery", {
          category,
          image_url: data.url,
          caption: { fr: "", en: "" },
          order: 0,
        });
        createdCount += 1;
        setQueue((q) =>
          q.map((x) => (x.key === item.key ? { ...x, status: "done", url: data.url } : x))
        );
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : "Échec de l'envoi";
        setQueue((q) => q.map((x) => (x.key === item.key ? { ...x, status: "error", error: msg } : x)));
      }
    }

    setBusy(false);
    if (createdCount > 0 && onComplete) onComplete(createdCount);
  };

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = "";
    if (files.length) startUploads(files);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
    if (files.length) startUploads(files);
  };

  const clearDone = () => setQueue((q) => q.filter((x) => x.status !== "done"));

  return (
    <div className="bg-[#FDFBF7] rounded-2xl border border-[#2D2A26]/10 p-5 mb-6" data-testid="multi-upload">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div>
          <h3 className="font-serif text-xl">Ajout rapide multi-photos</h3>
          <p className="text-xs text-[#4A3F35]/70 mt-1">Glissez plusieurs images ici, elles seront ajoutées en lot à la catégorie choisie.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="overline text-xs">Catégorie</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={busy}
            className="border border-[#2D2A26]/15 rounded-xl px-3 py-2 text-sm disabled:opacity-50"
            data-testid="multi-upload-category"
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/heic"
        multiple
        onChange={onPick}
        className="hidden"
        data-testid="multi-upload-input"
      />

      <div
        onClick={() => !busy && fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !busy) fileRef.current?.click(); }}
        className={`border-2 border-dashed rounded-xl py-10 px-6 flex flex-col items-center gap-2 cursor-pointer transition select-none ${
          dragOver ? "border-[#C87A5E] bg-[#C87A5E]/5" : "border-[#2D2A26]/20 hover:border-[#C87A5E]"
        } ${busy ? "opacity-60 cursor-wait" : ""}`}
        data-testid="multi-upload-dropzone"
      >
        <UploadCloud size={32} className="text-[#C87A5E]" />
        <div className="text-sm font-medium">
          {dragOver ? "Déposez pour ajouter" : "Glissez-déposez des images ou cliquez pour parcourir"}
        </div>
        <div className="text-xs text-[#4A3F35]/60">JPG, PNG, WebP — max 10 Mo par image</div>
      </div>

      {queue.length > 0 && (
        <div className="mt-5 space-y-1.5">
          <div className="flex items-center justify-between">
            <div className="overline text-xs">File d&apos;envoi ({queue.length})</div>
            <button
              type="button"
              onClick={clearDone}
              className="text-xs link-underline text-[#4A3F35]/70 hover:text-[#2D2A26]"
              data-testid="multi-upload-clear-done"
            >
              Effacer les succès
            </button>
          </div>
          <ul className="divide-y divide-[#2D2A26]/10 border border-[#2D2A26]/10 rounded-xl overflow-hidden">
            {queue.map((it) => (
              <li key={it.key} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`upload-item-${it.status}`}>
                <StatusIcon status={it.status} />
                <div className="flex-1 min-w-0">
                  <div className="truncate">{it.name}</div>
                  {it.error && <div className="text-xs text-red-700">{it.error}</div>}
                </div>
                <span className="text-xs text-[#4A3F35]/60">{formatSize(it.size)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === "done") return <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />;
  if (status === "error") return <AlertCircle size={16} className="text-red-700 shrink-0" />;
  if (status === "uploading") return <Loader2 size={16} className="text-[#C87A5E] animate-spin shrink-0" />;
  return <X size={16} className="text-[#4A3F35]/40 shrink-0" />;
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
